import { useState, useEffect, useRef } from 'react'
import type {
  MetadataGenerator,
  Metadata,
  GenerationOptions,
  ValidationResult,
} from '../contracts/metadataGenerator'
import { PLATFORM_CATEGORIES as CATEGORIES } from '../contracts/metadataGenerator'
import type { ImageFile } from '../contracts/uploadService'
import { AppIcons, ButtonIcon, AddIcon, DeleteIcon, SuccessIcon } from './icons'
import { ExifExtractorService } from '../services/ExifExtractorService'
import { reverseGeocodingService, type LocationInfo } from '../services/ReverseGeocodingService'
import { useCredits } from '../hooks/useCredits'
import { CreditBalance } from './billing/CreditBalance'

import type { ImageContext } from '../contracts/metadataGenerator'

interface MetadataEditorProps {
  metadataGenerator: MetadataGenerator
  images: ImageFile[]
  existingMetadata: Record<string, Metadata>
  imageContext: ImageContext
  onMetadataUpdate: (imageId: string, metadata: Metadata) => void
  isBatchGenerating: boolean
  batchProgress: { current: number; total: number }
  onBatchGeneratingChange: (generating: boolean) => void
  onBatchProgressChange: (progress: { current: number; total: number }) => void
  onAddMoreImages: (files: FileList) => void
  onDeleteImage: (imageId: string) => void
  onNavigateToExport?: () => void
}

// Get platform-specific keyword limit
const getKeywordLimit = (platform?: string): number => {
  switch (platform) {
    case 'shutterstock': return 50
    case 'adobe-stock': return 49
    case 'getty-images':
    case 'istock': return 50
    case 'generic':
    default: return 50
  }
}

// Get platform-specific field requirements
const getPlatformRequirements = (platform?: string) => {
  switch (platform) {
    case 'shutterstock':
      return {
        showDescription: true,
        showTitle: false,
        descriptionLabel: 'Description',
        descriptionRequired: true,
        maxDescriptionLength: 200,
        maxKeywords: 50,
        titleLabel: 'Title',
        titleRequired: false,
        maxTitleLength: 0,
        titlePlaceholder: '',
        titleHelp: '',
        showTitleHelp: false,
        descriptionPlaceholder: 'Describe the main subject and visual elements',
        descriptionHelp: 'Clear, concise description focusing on visual elements',
        showDescriptionHelp: true,
        categoryHelp: 'Select the most appropriate category for Shutterstock'
      }
    case 'adobe-stock':
      return {
        showDescription: false, // Adobe Stock uses Title field instead
        showTitle: true,
        descriptionLabel: 'Title',
        descriptionRequired: false,
        maxDescriptionLength: 0,
        maxKeywords: 49,
        titleLabel: 'Title',
        titleRequired: true,
        maxTitleLength: 200,
        titlePlaceholder: 'Enter descriptive title (serves as description)',
        titleHelp: 'This field serves as both title and description for Adobe Stock',
        showTitleHelp: true,
        descriptionPlaceholder: '',
        descriptionHelp: '',
        showDescriptionHelp: false,
        categoryHelp: 'Adobe Stock uses numeric categories - select the closest match'
      }
    case 'istock':
      return {
        showDescription: true,
        showTitle: true,
        descriptionLabel: 'Description',
        descriptionRequired: true,
        maxDescriptionLength: 1500,
        maxKeywords: 50,
        titleLabel: 'Title',
        titleRequired: false,
        maxTitleLength: 200,
        titlePlaceholder: 'Enter optional title',
        titleHelp: 'Optional title field for iStock',
        showTitleHelp: true,
        descriptionPlaceholder: 'Detailed description of the image content',
        descriptionHelp: 'iStock allows longer descriptions - be detailed and specific',
        showDescriptionHelp: true,
        categoryHelp: 'Choose the category that best fits your image content'
      }
    case 'getty-images':
      return {
        showDescription: true,
        showTitle: false,
        descriptionLabel: 'Description',
        descriptionRequired: true,
        maxDescriptionLength: 200,
        maxKeywords: 50,
        titleLabel: 'Title',
        titleRequired: false,
        maxTitleLength: 0,
        titlePlaceholder: '',
        titleHelp: '',
        showTitleHelp: false,
        descriptionPlaceholder: 'Describe the image content and context',
        descriptionHelp: 'Focus on newsworthy and editorial aspects for Getty Images',
        showDescriptionHelp: true,
        categoryHelp: 'Select category appropriate for Getty Images editorial focus'
      }
    default:
      return {
        showDescription: true,
        showTitle: true,
        descriptionLabel: 'Description',
        descriptionRequired: false,
        maxDescriptionLength: 200,
        maxKeywords: 50,
        titleLabel: 'Title',
        titleRequired: false,
        maxTitleLength: 200,
        titlePlaceholder: 'Enter image title',
        titleHelp: 'Optional title for the image',
        showTitleHelp: true,
        descriptionPlaceholder: 'Describe the image content',
        descriptionHelp: 'Describe what is shown in the image',
        showDescriptionHelp: true,
        categoryHelp: 'Select the most appropriate category'
      }
  }
}

export function DesktopMetadataEditor({ 
  metadataGenerator, 
  images, 
  existingMetadata, 
  imageContext, 
  onMetadataUpdate,
  isBatchGenerating,
  batchProgress,
  onBatchGeneratingChange,
  onBatchProgressChange,
  onAddMoreImages,
  onDeleteImage,
  onNavigateToExport
}: MetadataEditorProps) {
  const [selectedImage, setSelectedImage] = useState<ImageFile | null>(null)
  const [metadata, setMetadata] = useState<Metadata | null>(null)
  const [isGenerating, setIsGenerating] = useState(false)
  const [validation, setValidation] = useState<ValidationResult | null>(null)
  const [generationOptions, setGenerationOptions] = useState<GenerationOptions>({
    keywordLimit: getKeywordLimit(imageContext.platform),
    style: 'commercial',
    includeCompliance: true,
    confidenceThreshold: 0.7,
  })
  const [error, setError] = useState<string | null>(null)
  const [successMessage, setSuccessMessage] = useState<string | null>(null)
  const [showExportCTA, setShowExportCTA] = useState(false)
  const [isExifCollapsed, setIsExifCollapsed] = useState(false)
  const [currentLocationInfo, setCurrentLocationInfo] = useState<LocationInfo | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const exifExtractor = new ExifExtractorService()

  // Credit management
  const { 
    hasCredits, 
    hasSufficientCredits, 
    checkCanProcess, 
    deductForImageProcessing, 
    deductForBatchProcessing,
    isLoading: creditsLoading,
    error: creditsError 
  } = useCredits()

  // Get platform-specific categories or fallback to generic
  const platformCategories = imageContext.platform 
    ? CATEGORIES[imageContext.platform] 
    : CATEGORIES['generic']
  const availableCategories = [...platformCategories]
  
  // Get platform-specific field requirements
  const platformRequirements = getPlatformRequirements(imageContext.platform)

  useEffect(() => {
    if (images.length > 0 && !selectedImage) {
      setSelectedImage(images[0])
    }
  }, [images, selectedImage])

  // Load existing metadata when selecting an image
  useEffect(() => {
    if (selectedImage && existingMetadata[selectedImage.id]) {
      setMetadata(existingMetadata[selectedImage.id])
    } else {
      setMetadata(null)
    }
  }, [selectedImage, existingMetadata])

  // Load location info for EXIF display when image has GPS data
  useEffect(() => {
    if (selectedImage?.exifData?.location?.latitude && selectedImage?.exifData?.location?.longitude) {
      reverseGeocodingService.getLocationInfo(
        selectedImage.exifData.location.latitude,
        selectedImage.exifData.location.longitude
      )
      .then(locationInfo => {
        setCurrentLocationInfo(locationInfo)
      })
      .catch(error => {
        console.warn('Failed to load location info for display:', error)
        setCurrentLocationInfo(null)
      })
    } else {
      setCurrentLocationInfo(null)
    }
  }, [selectedImage])

  // Removed auto-generation - user must click Generate All button

  useEffect(() => {
    if (metadata) {
      const validationResult = metadataGenerator.validateMetadata(metadata, imageContext.platform)
      setValidation(validationResult)
    }
  }, [metadata, metadataGenerator, imageContext.platform])

  // Update keyword limit when platform changes
  useEffect(() => {
    setGenerationOptions(prev => ({
      ...prev,
      keywordLimit: getKeywordLimit(imageContext.platform)
    }))
  }, [imageContext.platform])

  // Helper function to enhance context with EXIF data (date and location) for editorial images
  const enhanceContextWithExifData = async (image: ImageFile, context: ImageContext): Promise<ImageContext> => {
    let enhancedContext = { ...context }

    // Add EXIF date if not already set
    if (!enhancedContext.date && image.exifData?.dateTime?.dateTimeOriginal) {
      enhancedContext.date = image.exifData.dateTime.dateTimeOriginal
    }

    // Add EXIF location if not already set and GPS data is available
    if (!enhancedContext.location && image.exifData?.location?.latitude && image.exifData?.location?.longitude) {
      try {
        const locationInfo = await reverseGeocodingService.getLocationInfo(
          image.exifData.location.latitude,
          image.exifData.location.longitude
        )
        if (locationInfo?.formattedAddress) {
          enhancedContext.location = locationInfo.formattedAddress
        }
      } catch (error) {
        console.warn('Failed to get location info:', error)
        // Continue without location enhancement
      }
    }

    return enhancedContext
  }

  const handleGenerateMetadata = async () => {
    if (!selectedImage) return

    // Check if user has credits before processing
    if (!checkCanProcess(1)) {
      setError('Insufficient credits. Please upgrade your plan to continue processing images.')
      return
    }

    try {
      setIsGenerating(true)
      setError(null)

      // Deduct credit for image processing before AI generation
      await deductForImageProcessing(selectedImage.id, 'ai_metadata_generation')

      // Enhance context with EXIF data if available
      const enhancedContext = await enhanceContextWithExifData(selectedImage, imageContext)

      // Always use maximum keywords for the platform
      const optionsWithMaxKeywords = {
        ...generationOptions,
        keywordLimit: getKeywordLimit(imageContext.platform),
        context: enhancedContext
      }

      const generatedMetadata = await metadataGenerator.generateMetadata(
        selectedImage,
        optionsWithMaxKeywords,
      )

      setMetadata(generatedMetadata)
      onMetadataUpdate(selectedImage.id, generatedMetadata)
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to generate metadata'
      
      // Check if it's a credit-related error
      if (errorMessage.includes('insufficient') || errorMessage.includes('credit')) {
        setError('Insufficient credits. Please upgrade your plan to continue processing images.')
      } else {
        setError(errorMessage)
      }
    } finally {
      setIsGenerating(false)
    }
  }

  const handleBatchGenerate = async () => {
    // Only generate metadata for images that don't have it yet
    const imagesToProcess = images.filter(image => !existingMetadata[image.id])
    
    if (imagesToProcess.length === 0) {
      setSuccessMessage('All images already have metadata!')
      setTimeout(() => setSuccessMessage(null), 3000)
      return
    }

    // Check if user has sufficient credits for batch processing
    if (!checkCanProcess(imagesToProcess.length)) {
      setError(`Insufficient credits for batch processing ${imagesToProcess.length} images. Please upgrade your plan or process fewer images.`)
      return
    }

    try {
      onBatchGeneratingChange(true)
      setError(null)
      setSuccessMessage(null)
      setShowExportCTA(false)
      onBatchProgressChange({ current: 0, total: imagesToProcess.length })

      // Generate unique batch ID for credit deduction
      const batchId = `batch_${Date.now()}_${Math.random().toString(36).substring(2, 8)}`

      // Deduct credits for entire batch upfront to prevent partial processing
      await deductForBatchProcessing(batchId, imagesToProcess.length, 'bulk_metadata_generation')

      // Generate metadata one by one to show progress
      const results: Record<string, Metadata> = {}
      let successCount = 0
      
      for (let i = 0; i < imagesToProcess.length; i++) {
        const image = imagesToProcess[i]
        
        try {
          // Enhance context with EXIF data if available
          const enhancedContext = await enhanceContextWithExifData(image, imageContext)

          // Always use maximum keywords for the platform
          const optionsWithMaxKeywords = {
            ...generationOptions,
            keywordLimit: getKeywordLimit(imageContext.platform),
            context: enhancedContext
          }
          const generatedMetadata = await metadataGenerator.generateMetadata(image, optionsWithMaxKeywords)
          results[image.id] = generatedMetadata
          onMetadataUpdate(image.id, generatedMetadata)
          successCount++
          
          // Update progress after successful generation
          onBatchProgressChange({ current: successCount, total: imagesToProcess.length })
          
          // Update display if this is the currently selected image
          if (selectedImage?.id === image.id) {
            setMetadata(generatedMetadata)
          }
          
          // Add small delay to respect API rate limits
          if (i < imagesToProcess.length - 1) {
            await new Promise(resolve => setTimeout(resolve, 1000))
          }
        } catch (imageError) {
          console.error(`Failed to generate metadata for ${image.name}:`, imageError)
          // Continue with other images - progress is only updated on success
        }
      }
      
      // Show success message and export CTA
      if (successCount > 0) {
        const allImagesHaveMetadata = images.every(image => existingMetadata[image.id])
        const metadataCount = Object.keys(existingMetadata).length
        const hasSignificantProgress = metadataCount >= Math.max(1, Math.floor(images.length * 0.5)) // At least 50% or 1 image
        
        if (allImagesHaveMetadata) {
          setSuccessMessage(`🎉 All ${images.length} images now have metadata! Ready to export.`)
          setShowExportCTA(true)
        } else if (hasSignificantProgress && successCount === imagesToProcess.length) {
          // Just finished processing all remaining images and have significant metadata
          setSuccessMessage(`✅ Generated metadata for ${successCount} image${successCount > 1 ? 's' : ''}! ${metadataCount}/${images.length} images ready.`)
          setShowExportCTA(true)
        } else {
          setSuccessMessage(`✅ Generated metadata for ${successCount} image${successCount > 1 ? 's' : ''}!`)
        }
        
        // Hide success message after 5 seconds
        setTimeout(() => setSuccessMessage(null), 5000)
      }
      
      // Final progress update shows actual successful completions
      onBatchProgressChange({ current: successCount, total: imagesToProcess.length })
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to generate batch metadata')
    } finally {
      onBatchGeneratingChange(false)
      onBatchProgressChange({ current: 0, total: 0 })
    }
  }

  const handleMetadataChange = async (field: keyof Metadata, value: any) => {
    if (!metadata || !selectedImage) return

    let updatedMetadata = { ...metadata, [field]: value, isEdited: true, editedAt: new Date() }
    
    // If commercial use setting changed, regenerate the title to add/remove location/date
    if (field === 'isCommercial') {
      try {
        // Create a mock analysis from existing metadata for title regeneration
        const mockAnalysis = {
          subjects: metadata.keywords.slice(0, 4), // Use first few keywords as subjects
          dominantColors: ['#000000'], // Mock color
          composition: 'landscape' as const,
          brightness: 'medium' as const,
          complexity: 'moderate' as const,
          style: 'photography' as const,
        }

        // Regenerate title based on new commercial setting
        const newTitle = await generateTitleBasedOnCommercialUse(
          selectedImage.name,
          mockAnalysis,
          generationOptions.style,
          imageContext,
          value // new commercial setting
        )
        
        updatedMetadata = { ...updatedMetadata, title: newTitle }
      } catch (error) {
        console.error('Failed to regenerate title:', error)
        // Continue with the original update if title regeneration fails
      }
    }
    
    setMetadata(updatedMetadata)
    onMetadataUpdate(selectedImage.id, updatedMetadata)
  }

  // Helper function to generate title based on commercial use setting
  const generateTitleBasedOnCommercialUse = (
    filename: string,
    analysis: any,
    style: string,
    context: typeof imageContext,
    isCommercial: boolean
  ): string => {
    const baseName = filename.replace(/\.(jpg|jpeg|png|webp|gif)$/i, '').replace(/[-_]/g, ' ')
    
    // Filter keywords to get meaningful subjects (avoid location keywords and generic terms)
    const locationTerms = [
      context?.location?.toLowerCase(),
      ...(context?.location?.split(',').map(s => s.trim().toLowerCase()) || []),
      'barcelona', 'spain', 'photography', 'photo', 'image', 'stock'
    ].filter(Boolean)
    
    const meaningfulKeywords = analysis.subjects.filter((keyword: string) => 
      !locationTerms.includes(keyword.toLowerCase()) && 
      keyword.length > 2 &&
      !['the', 'and', 'or', 'in', 'on', 'at', 'to', 'for', 'of'].includes(keyword.toLowerCase())
    )
    
    // Use the most relevant keywords as subjects
    const subjects = meaningfulKeywords.slice(0, 2).join(' and ') || 'vehicle model'

    // Only include context (location/date) in title for editorial content
    let titlePrefix = ''
    if (!isCommercial) {
      if (context?.location && context?.month && context?.year) {
        const months = ['January', 'February', 'March', 'April', 'May', 'June',
                       'July', 'August', 'September', 'October', 'November', 'December']
        titlePrefix = `${context.location} - ${months[context.month - 1]} ${context.year}: `
      } else if (context?.location && context?.date) {
        const date = new Date(context.date)
        const months = ['January', 'February', 'March', 'April', 'May', 'June',
                       'July', 'August', 'September', 'October', 'November', 'December']
        const monthName = months[date.getMonth()]
        const day = date.getDate()
        const year = date.getFullYear()
        titlePrefix = `${context.location} - ${monthName} ${day}, ${year}: `
      } else if (context?.location) {
        titlePrefix = `${context.location} - `
      }
    }

    let title = ''
    const capitalizeWords = (str: string) => str.split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ')

    switch (style) {
      case 'creative':
        title = context?.title ? `${context.title}` : `${capitalizeWords(subjects)} in ${analysis.composition} composition`
        break
      case 'commercial':
        title = context?.title ? `${context.title}` : `Professional ${subjects} stock photo`
        break
      default:
        title = context?.title ? `${context.title}` : `${capitalizeWords(subjects)} model display`
    }

    const fullTitle = titlePrefix + title
    // Apply length limit (simplified version)
    return fullTitle.length > 200 ? fullTitle.substring(0, 197) + '...' : fullTitle
  }

  const [newKeyword, setNewKeyword] = useState('')

  const handleKeywordAdd = () => {
    if (!metadata || !newKeyword.trim()) return
    const trimmedKeyword = newKeyword.trim()
    if (metadata.keywords.includes(trimmedKeyword)) return // Avoid duplicates
    const updatedKeywords = [...metadata.keywords, trimmedKeyword]
    handleMetadataChange('keywords', updatedKeywords)
    setNewKeyword('') // Clear input after adding
  }

  const handleKeywordInputKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      handleKeywordAdd()
    }
  }

  const handleKeywordChange = (index: number, value: string) => {
    if (!metadata) return
    const updatedKeywords = [...metadata.keywords]
    updatedKeywords[index] = value.trim()
    handleMetadataChange('keywords', updatedKeywords.filter(k => k.length > 0))
  }

  const handleKeywordRemove = (index: number) => {
    if (!metadata) return
    const updatedKeywords = metadata.keywords.filter((_, i) => i !== index)
    handleMetadataChange('keywords', updatedKeywords)
  }

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files
    if (files && files.length > 0) {
      onAddMoreImages(files)
      // Reset the input so the same files can be selected again if needed
      if (fileInputRef.current) {
        fileInputRef.current.value = ''
      }
    }
  }

  const handleAddMoreImagesClick = () => {
    fileInputRef.current?.click()
  }

  if (images.length === 0) {
    return (
      <div className="bg-white rounded-xl shadow-sm border p-4 lg:p-6">
        <div className="text-center py-8 text-gray-500">
          <p>No images to edit metadata for</p>
          <p className="text-sm">Upload some images to get started</p>
        </div>
      </div>
    )
  }

  return (
    <div className="bg-white rounded-xl shadow-sm border flex flex-col">
      {/* Header */}
      <div className="border-b px-4 lg:px-6 py-4">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0">
          <div className="flex-1">
            <h2 className="text-lg lg:text-xl font-semibold text-gray-900">Metadata Editor</h2>
            {imageContext.platform && (
              <p className="text-sm text-gray-600 mt-1">
                Optimized for <span className="font-medium capitalize">{imageContext.platform.replace('-', ' ')}</span>
              </p>
            )}
          </div>
          <div className="flex flex-col lg:flex-row lg:items-center space-y-3 lg:space-y-0 lg:space-x-4">
            {/* Credit Balance Display */}
            <CreditBalance 
              minimal={true} 
              className="lg:max-w-xs"
              showUpgradePrompt={!hasCredits}
            />
            <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-3">
            <button
              onClick={handleAddMoreImagesClick}
              className="border border-blue-500 text-blue-600 px-4 py-3 lg:py-2 rounded-xl hover:bg-blue-50 transition-colors flex items-center justify-center sm:justify-start space-x-2 font-medium text-base"
              style={{ minHeight: '44px' }}
            >
              <AddIcon />
              <span>Add More Images</span>
            </button>
            <button
              onClick={handleBatchGenerate}
              disabled={
                isBatchGenerating || 
                images.filter(image => !existingMetadata[image.id]).length === 0 ||
                !checkCanProcess(images.filter(image => !existingMetadata[image.id]).length)
              }
              className="bg-blue-600 text-white px-6 py-3 lg:py-2 rounded-xl hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors font-medium text-base"
              style={{ minHeight: '44px' }}
              title={
                !checkCanProcess(images.filter(image => !existingMetadata[image.id]).length)
                  ? 'Insufficient credits for batch processing'
                  : undefined
              }
            >
              {isBatchGenerating 
                ? 'Generating...' 
                : images.filter(image => !existingMetadata[image.id]).length === 0
                ? 'All Complete'
                : `Generate Missing (${images.filter(image => !existingMetadata[image.id]).length})`
              }
            </button>
            </div>
          </div>
          
          {/* Hidden file input */}
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleFileSelect}
            accept="image/*"
            multiple
            className="hidden"
          />
        </div>

        {/* Progress Bar */}
        {isBatchGenerating && batchProgress.total > 0 && (
          <div className="mt-4 bg-blue-50 border border-blue-200 rounded-xl p-3 lg:p-4">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-2 space-y-1 sm:space-y-0">
              <span className="text-xs sm:text-sm font-medium text-blue-900">
                Generating metadata... ({batchProgress.current} successful of {batchProgress.total} images)
              </span>
              <span className="text-xs sm:text-sm text-blue-700 font-medium">
                {Math.round((batchProgress.current / batchProgress.total) * 100)}%
              </span>
            </div>
            <div className="w-full bg-blue-200 rounded-full h-2">
              <div 
                className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                style={{ width: `${(batchProgress.current / batchProgress.total) * 100}%` }}
              ></div>
            </div>
          </div>
        )}


        {/* Success Message */}
        {successMessage && (
          <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-xl">
            <p className="text-green-600 text-sm font-medium">{successMessage}</p>
          </div>
        )}

        {/* Export CTA */}
        {showExportCTA && (
          <div className="mt-4 p-6 bg-gradient-to-r from-green-50 to-blue-50 border-2 border-green-200 rounded-xl shadow-sm">
            <div className="text-center">
              <div className="mb-4">
                <div className="inline-flex items-center justify-center w-12 h-12 bg-green-100 rounded-full mb-3">
                  <AppIcons.Download className="w-6 h-6 text-green-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  🎉 Your metadata is ready!
                </h3>
                <p className="text-gray-600 text-sm mb-4">
                  Export your images with AI-generated metadata to your preferred stock marketplace
                </p>
              </div>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <button
                  onClick={() => {
                    setShowExportCTA(false)
                    onNavigateToExport?.()
                  }}
                  className="bg-gradient-to-r from-green-600 to-blue-600 text-white py-3 px-8 rounded-xl hover:from-green-700 hover:to-blue-700 transition-all duration-200 transform hover:scale-105 font-semibold shadow-lg flex items-center justify-center space-x-2"
                >
                  <AppIcons.Download className="w-5 h-5" />
                  <span>Export Now</span>
                </button>
                <button
                  onClick={() => setShowExportCTA(false)}
                  className="text-gray-600 hover:text-gray-800 py-3 px-6 rounded-xl hover:bg-white/50 transition-colors text-sm"
                >
                  Continue editing
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Generation Options - Hidden, using commercial style by default */}
      </div>

      <div className="flex flex-col lg:flex-row">
        {/* Image List */}
        <div className="w-full lg:w-1/3 lg:border-r border-b lg:border-b-0 flex-shrink-0 flex flex-col">
          <div className="overflow-y-auto flex-1">
          {images.map((image) => {
            const hasMetadata = existingMetadata[image.id] !== undefined
            // const isProcessing = isBatchGenerating && batchProgress.current < images.findIndex(img => img.id === image.id)
            const isCurrentlyProcessing = isBatchGenerating && batchProgress.current === images.findIndex(img => img.id === image.id)
            
            return (
              <div
                key={image.id}
                className={`p-3 lg:p-3 border-b cursor-pointer hover:bg-gray-50 transition-colors group ${
                  selectedImage?.id === image.id ? 'bg-blue-50 border-blue-200' : ''
                }`}
                onClick={() => setSelectedImage(image)}
              >
                <div className="flex items-center space-x-3">
                  <div className="relative">
                    <img
                      src={image.preview}
                      alt={image.name}
                      className="w-16 h-16 lg:w-12 lg:h-12 object-cover rounded"
                    />
                    {/* Status indicators */}
                    {hasMetadata && (
                      <div className="absolute -top-1 -right-1 w-4 h-4 bg-green-500 rounded-full flex items-center justify-center">
                        <SuccessIcon className="w-3 h-3 text-white" />
                      </div>
                    )}
                    {isCurrentlyProcessing && (
                      <div className="absolute -top-1 -right-1 w-4 h-4 bg-blue-500 rounded-full animate-pulse">
                        <div className="w-full h-full bg-blue-400 rounded-full animate-ping"></div>
                      </div>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center space-x-2">
                      <p className="text-sm lg:text-sm font-medium text-gray-900 truncate">{image.name}</p>
                      {hasMetadata && <SuccessIcon className="w-3 h-3 text-green-600" />}
                      {isCurrentlyProcessing && <span className="text-xs text-blue-600 font-medium">...</span>}
                    </div>
                    <p className="text-xs text-gray-500">
                      {Math.round(image.size / 1024)} KB • {image.dimensions?.width || 0}×{image.dimensions?.height || 0}
                      {image.dimensions?.duration && (
                        <> • {Math.round(image.dimensions.duration)}s</>
                      )}
                    </p>
                  </div>
                  <button
                    onClick={(e) => {
                      e.stopPropagation()
                      onDeleteImage(image.id)
                      // If this was the selected image, select the first remaining image
                      if (selectedImage?.id === image.id && images.length > 1) {
                        const remainingImages = images.filter(img => img.id !== image.id)
                        if (remainingImages.length > 0) {
                          setSelectedImage(remainingImages[0])
                        }
                      }
                    }}
                    className="opacity-0 group-hover:opacity-100 lg:opacity-0 lg:group-hover:opacity-100 transition-opacity hover:bg-red-50 rounded-xl w-11 h-11 flex items-center justify-center"
                    title="Delete image"
                  >
                    <AppIcons.X className="w-5 h-5 text-red-500" />
                  </button>
                </div>
              </div>
            )
          })}
          </div>
        </div>

        {/* Metadata Form */}
        <div className="flex-1 p-4 lg:p-6">
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-xl">
              <p className="text-red-600 text-sm">{error}</p>
            </div>
          )}

          {selectedImage && (
            <div className="space-y-4">
              <div className="flex flex-col sm:flex-row sm:items-center space-y-3 sm:space-y-0 sm:space-x-4">
                <img
                  src={selectedImage.preview}
                  alt={selectedImage.name}
                  className="w-full sm:w-24 h-32 sm:h-24 object-cover rounded lg:w-20 lg:h-20"
                />
                <div>
                  <h3 className="font-medium text-gray-900">{selectedImage.name}</h3>
                  <p className="text-sm text-gray-500">
                    {selectedImage.dimensions?.width || 0}×{selectedImage.dimensions?.height || 0}
                    {selectedImage.dimensions?.duration && (
                      <> • {Math.round(selectedImage.dimensions.duration)}s</>
                    )}
                    {' • '}{Math.round(selectedImage.size / 1024)} KB
                  </p>
                </div>
              </div>

              {metadata && (
                <div className="space-y-4">
                  {/* Title - Show if platform requires it */}
                  {platformRequirements.showTitle && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {platformRequirements.titleLabel}
                        {platformRequirements.titleRequired && <span className="text-red-500 ml-1">*</span>}
                      </label>
                      <input
                        type="text"
                        value={metadata.title || ''}
                        onChange={(e) => handleMetadataChange('title', e.target.value)}
                        className="w-full p-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-base"
                        style={{ minHeight: '44px' }}
                        placeholder={platformRequirements.titlePlaceholder}
                        maxLength={platformRequirements.maxTitleLength}
                      />
                      {platformRequirements.showTitleHelp && (
                        <p className="text-xs text-gray-500 mt-1">
                          {platformRequirements.titleHelp} (Max {platformRequirements.maxTitleLength} characters)
                        </p>
                      )}
                      {metadata.title && metadata.title.length > (platformRequirements.maxTitleLength * 0.9) && (
                        <p className="text-xs text-orange-600 mt-1">
                          {metadata.title.length}/{platformRequirements.maxTitleLength} characters
                        </p>
                      )}
                    </div>
                  )}

                  {/* Description - Show if platform requires it */}
                  {platformRequirements.showDescription && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {platformRequirements.descriptionLabel}
                        {platformRequirements.descriptionRequired && <span className="text-red-500 ml-1">*</span>}
                      </label>
                      <textarea
                        value={metadata.description || ''}
                        onChange={(e) => handleMetadataChange('description', e.target.value)}
                        rows={3}
                        className="w-full p-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-base"
                        style={{ minHeight: '88px' }}
                        placeholder={platformRequirements.descriptionPlaceholder}
                        maxLength={platformRequirements.maxDescriptionLength}
                      />
                      {platformRequirements.showDescriptionHelp && (
                        <p className="text-xs text-gray-500 mt-1">
                          {platformRequirements.descriptionHelp} (Max {platformRequirements.maxDescriptionLength} characters)
                        </p>
                      )}
                      {metadata.description && metadata.description.length > (platformRequirements.maxDescriptionLength * 0.9) && (
                        <p className="text-xs text-orange-600 mt-1">
                          {metadata.description.length}/{platformRequirements.maxDescriptionLength} characters
                        </p>
                      )}
                    </div>
                  )}

                  {/* Category */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                    <select
                      value={metadata.category}
                      onChange={(e) => handleMetadataChange('category', e.target.value)}
                      className="w-full p-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-base"
                      style={{ minHeight: '44px' }}
                    >
                      {availableCategories.map((category) => (
                        <option key={category} value={category}>
                          {category}
                        </option>
                      ))}
                    </select>
                    {platformRequirements.categoryHelp && (
                      <p className="text-xs text-gray-500 mt-1">
                        {platformRequirements.categoryHelp}
                      </p>
                    )}
                  </div>

                  {/* Keywords */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Keywords ({metadata.keywords.length}/{platformRequirements.maxKeywords})
                      <span className="text-red-500 ml-1">*</span>
                    </label>
                    
                    {/* Add keyword input */}
                    <div className="flex flex-col sm:flex-row gap-2 mb-3">
                      <input
                        type="text"
                        value={newKeyword}
                        onChange={(e) => setNewKeyword(e.target.value)}
                        onKeyPress={handleKeywordInputKeyPress}
                        className="flex-1 p-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-base"
                        style={{ minHeight: '44px' }}
                        placeholder="Enter a new keyword..."
                      />
                      <button
                        onClick={handleKeywordAdd}
                        disabled={!newKeyword.trim()}
                        className="px-6 py-3 bg-blue-600 text-white rounded-xl hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors font-medium text-base"
                        style={{ minHeight: '44px', minWidth: '80px' }}
                      >
                        Add
                      </button>
                    </div>
                    
                    {/* Keyword pills display */}
                    <div className="flex flex-wrap gap-2 p-3 border border-gray-300 rounded-xl bg-gray-50 min-h-[80px] lg:min-h-[60px] max-h-60 lg:max-h-40 overflow-y-auto">
                      {metadata.keywords.map((keyword, index) => (
                        <div
                          key={index}
                          className="inline-flex items-center bg-blue-100 text-blue-800 text-sm font-medium px-3 py-2 lg:py-1 rounded-full border border-blue-200 hover:bg-blue-200 transition-colors"
                        >
                          <span className="select-none">{keyword}</span>
                          <button
                            onClick={() => handleKeywordRemove(index)}
                            onTouchStart={(e) => e.currentTarget.style.transform = 'scale(0.95)'}
                            onTouchEnd={(e) => e.currentTarget.style.transform = 'scale(1)'}
                            className="ml-2 text-blue-600 active:text-white active:bg-blue-700 hover:text-blue-800 hover:bg-blue-700 hover:text-white text-xs w-6 h-6 lg:w-4 lg:h-4 flex items-center justify-center rounded-full transition-all duration-150 touch-manipulation"
                            style={{ minHeight: '24px', minWidth: '24px', WebkitTapHighlightColor: 'transparent' }}
                            title="Remove keyword"
                          >
                            ✕
                          </button>
                        </div>
                      ))}
                      
                      {metadata.keywords.length === 0 && (
                        <span className="text-gray-400 text-sm italic">
                          No keywords added yet. Enter a keyword above and click "Add".
                        </span>
                      )}
                    </div>
                    
                    {/* Platform-specific keyword guidance */}
                    <p className="text-xs text-gray-500 mt-2">
                      Max {platformRequirements.maxKeywords} keywords for {imageContext.platform?.replace('-', ' ') || 'this platform'}.
                      {metadata.keywords.length > platformRequirements.maxKeywords && (
                        <span className="text-red-600 ml-1 font-medium">
                          ({metadata.keywords.length - platformRequirements.maxKeywords} over limit)
                        </span>
                      )}
                    </p>
                  </div>

                  {/* Commercial Use */}
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <label className="flex items-center cursor-pointer p-2 rounded-xl hover:bg-gray-50" style={{ minHeight: '44px' }}>
                        <input
                          type="checkbox"
                          checked={metadata.isCommercial}
                          onChange={(e) => handleMetadataChange('isCommercial', e.target.checked)}
                          className="mr-3 w-4 h-4"
                        />
                        <span className="text-sm lg:text-sm font-medium">Commercial Use</span>
                      </label>
                      {!metadata.isCommercial && (
                        <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded-full font-medium">
                          Editorial
                        </span>
                      )}
                    </div>
                    {!metadata.isCommercial && (
                      <div className="text-xs text-gray-600 bg-gray-50 p-2 rounded border border-gray-200 flex items-center space-x-2">
                        <ButtonIcon icon={AppIcons.FileText} className="w-4 h-4 text-gray-500" />
                        <span>Editorial content requires proper attribution and cannot be used commercially without releases.</span>
                      </div>
                    )}
                  </div>
                  
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 lg:gap-4">
                    <label className="flex items-center cursor-pointer p-2 rounded-xl hover:bg-gray-50" style={{ minHeight: '44px' }}>
                      <input
                        type="checkbox"
                        checked={metadata.hasModelRelease}
                        onChange={(e) => handleMetadataChange('hasModelRelease', e.target.checked)}
                        className="mr-3 w-4 h-4"
                      />
                      <span className="text-sm font-medium">Model Release</span>
                    </label>
                    <label className="flex items-center cursor-pointer p-2 rounded-xl hover:bg-gray-50" style={{ minHeight: '44px' }}>
                      <input
                        type="checkbox"
                        checked={metadata.hasPropertyRelease}
                        onChange={(e) => handleMetadataChange('hasPropertyRelease', e.target.checked)}
                        className="mr-3 w-4 h-4"
                      />
                      <span className="text-sm font-medium">Property Release</span>
                    </label>
                  </div>

                  {/* Validation Results */}
                  {validation && !validation.isValid && (
                    <div className="p-3 bg-red-50 border border-red-200 rounded-xl">
                      <h4 className="font-medium text-red-800 mb-2">Validation Errors</h4>
                      {validation.errors.map((error, index) => (
                        <p key={index} className="text-sm text-red-600">
                          • {error.message}
                        </p>
                      ))}
                    </div>
                  )}

                  {validation && validation.warnings.length > 0 && (
                    <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-xl">
                      <h4 className="font-medium text-yellow-800 mb-2">Warnings</h4>
                      {validation.warnings.map((warning, index) => (
                        <div key={index} className="text-sm text-yellow-600">
                          <p>• {warning.message}</p>
                          {warning.suggestion && (
                            <p className="ml-4 italic flex items-center">
                              <AppIcons.Info className="w-4 h-4 mr-1 text-blue-500" />
                              {warning.suggestion}
                            </p>
                          )}
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Metadata Info */}
                  <div className="text-xs text-gray-500 pt-2 border-t">
                    <div className="flex justify-between">
                      <span>Confidence: {Math.round(metadata.confidence * 100)}%</span>
                      <span>Generated: {metadata.generatedAt.toLocaleString()}</span>
                      {metadata.isEdited && metadata.editedAt && (
                        <span>Edited: {metadata.editedAt.toLocaleString()}</span>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {!metadata && (
                <div className="text-center py-6 lg:py-8 text-gray-500 px-4">
                  <p className="text-base lg:text-lg font-medium mb-2">No metadata generated yet</p>
                  <p className="text-sm text-gray-400">Use "Generate All" button to create metadata for all images</p>
                </div>
              )}

              {/* EXIF Data Section - Moved to bottom */}
              {selectedImage.exifData && (
                <div className="border border-gray-200 rounded-xl p-4 bg-gray-50 mt-4">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="font-medium text-gray-900 flex items-center space-x-2">
                      <span>📷</span>
                      <span>EXIF Data</span>
                      <div className="flex space-x-2">
                        {!imageContext.date && selectedImage.exifData.dateTime?.dateTimeOriginal && (
                          <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded-full">
                            Date will be used for metadata
                          </span>
                        )}
                        {!imageContext.location && selectedImage.exifData.location?.latitude && selectedImage.exifData.location?.longitude && (
                          <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded-full">
                            📍 Location will be used for metadata
                          </span>
                        )}
                      </div>
                    </h4>
                    <button
                      onClick={() => setIsExifCollapsed(!isExifCollapsed)}
                      className="text-sm text-gray-500 hover:text-gray-700 transition-colors"
                    >
                      {isExifCollapsed ? 'Show' : 'Hide'}
                    </button>
                  </div>
                  
                  {/* Prominent Location Display */}
                  {currentLocationInfo && (
                    <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-xl">
                      <div className="flex items-start space-x-3">
                        <div className="flex-shrink-0">
                          <span className="text-green-600 text-lg">📍</span>
                        </div>
                        <div className="flex-1">
                          <h5 className="text-sm font-medium text-green-800 mb-1">Location Data Available</h5>
                          <p className="text-sm text-green-700 mb-2">{currentLocationInfo.formattedAddress}</p>
                          <p className="text-xs text-green-600">
                            GPS: {currentLocationInfo.coordinates.latitude.toFixed(6)}, {currentLocationInfo.coordinates.longitude.toFixed(6)}
                          </p>
                          {!imageContext.location && (
                            <p className="text-xs text-green-600 mt-2 italic">
                              ✨ This location will be automatically added to editorial metadata
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                  
                  {!isExifCollapsed && (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm">
                      {Object.entries(exifExtractor.formatForDisplay(selectedImage.exifData)).map(([key, value]) => (
                        <div key={key} className="flex justify-between py-1 border-b border-gray-200 last:border-b-0">
                          <span className="font-medium text-gray-600">{key}:</span>
                          <span className="text-gray-900 text-right">{value}</span>
                        </div>
                      ))}
                      {/* Add location name if available and not already shown in coordinates */}
                      {currentLocationInfo?.formattedAddress && !exifExtractor.formatForDisplay(selectedImage.exifData)['Location'] && (
                        <div className="flex justify-between py-1 border-b border-gray-200 last:border-b-0">
                          <span className="font-medium text-gray-600">Location:</span>
                          <span className="text-gray-900 text-right">{currentLocationInfo.formattedAddress}</span>
                        </div>
                      )}
                    </div>
                  )}
                  
                  {!isExifCollapsed && selectedImage.exifData.hasErrors && selectedImage.exifData.errors && selectedImage.exifData.errors.length > 0 && (
                    <div className="mt-3 p-2 bg-orange-50 border border-orange-200 rounded">
                      <p className="text-xs text-orange-700">
                        <strong>EXIF Warnings:</strong> {selectedImage.exifData.errors.join(', ')}
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
